import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Spin, Card, Radio, Table, Button } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import GlobalFilterBar from '../components/common/GlobalFilterBar';
import DownloadButton from '../components/common/DownloadButton';
import { useFilters } from '../context/FilterContext';
import { formatChartDateAxis, formatNumberKM, tooltipSorter, aggregateChartData, formatTableData, getTableColumns } from '../utils/formatters';
import { exportCardAsPng, exportTableAsCsv } from '../utils/downloadUtils';
import { getVolumeDashboard } from '../services/api';
import dayjs from 'dayjs';

const renderCustomLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
      {payload.map((entry, index) => {
        if (entry.value === 'base' || entry.value === 'dummy') return null;
        let color = entry.color;
        if (entry.value === 'Over-Injection (OI)') color = '#ef4444';
        if (entry.value === 'Under-Injection (UI)') color = '#0288D1';
        const isLine = entry.type === 'line';
        return (
          <span key={`legend-${index}`} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
            <span style={{
              display: 'inline-block',
              width: isLine ? 16 : 12,
              height: isLine ? 4 : 12,
              backgroundColor: color,
              marginRight: 6,
              borderRadius: isLine ? 0 : 2
            }} />
            <span style={{ fontSize: 12, color: '#555' }}>{entry.value}</span>
          </span>
        );
      })}
    </div>
  );
};

const formatVolumeNumber = (val) => {
  const num = Number(val) || 0;
  if (Math.abs(num) >= 10000) {
    return `${(num / 1000000).toFixed(2)} MU`;
  }
  return `${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`;
};

function KpiCardV2({ title, value, subtitle, color }) {
  return (
    <Card size="small" style={{ background: color || '#f0f3ff', borderRadius: 8, height: '100%', padding: '4px 8px', flex: '0 0 160px' }} bodyStyle={{ padding: 0 }}>
      <Typography.Text type="secondary" style={{ fontSize: 11, fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Typography.Text>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1B4F72', marginTop: 4, whiteSpace: 'nowrap' }}>
        {value}
      </div>
      {subtitle && <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{subtitle}</div>}
    </Card>
  );
} function DonutChartWithLegend({ data, colors, title }) {
  const [isExchangeExpanded, setIsExchangeExpanded] = useState(false);

  const total = useMemo(() => {
    return data.reduce((acc, curr) => acc + Math.abs(curr.value || 0), 0);
  }, [data]);

  const netTotal = useMemo(() => {
    return data.reduce((acc, curr) => acc + (curr.value || 0), 0);
  }, [data]);

  const pieChartData = useMemo(() => {
    return data.map(item => ({
      ...item,
      displayValue: Math.abs(item.value || 0)
    }));
  }, [data]);

  const sortedLegends = useMemo(() => {
    return [...data].sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
  }, [data]);

  const formatVolume = (val) => {
    return formatVolumeNumber(val);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0].payload;
      const val = Math.abs(entry.value || 0);
      const pct = total > 0 ? (val / total) * 100 : 0;
      const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
      const formattedVal = formatVolume(entry.value);

      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #e0e0e0',
          padding: '8px 12px',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '12px',
          color: '#333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key] || '#8884d8' }} />
            <span>{entry.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span>Volume:</span>
            <span style={{ fontWeight: 600 }}>{formattedVal} ({pctString})</span>
          </div>
          {entry.breakdown && (
            <div style={{ borderTop: '1px solid #eaeaea', paddingTop: 6, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* GDAM */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                  <span>GDAM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatVolume(entry.breakdown.gdam)} ({entry.value > 0 ? `${((entry.breakdown.gdam / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
              {/* DAM */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E67E22' }} />
                  <span>DAM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatVolume(entry.breakdown.dam)} ({entry.value > 0 ? `${((entry.breakdown.dam / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
              {/* RTM */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#3498DB' }} />
                  <span>RTM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatVolume(entry.breakdown.rtm)} ({entry.value > 0 ? `${((entry.breakdown.rtm / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 32, padding: '0 16px', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: '260px', height: 260, flex: '1 1 calc(50% - 16px)', minWidth: '220px' }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Total</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginTop: 2 }}>
            {formatVolume(netTotal)}
          </div>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieChartData}
              dataKey="displayValue"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={95}
              paddingAngle={2}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.key] || '#8884d8'} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 1000 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div style={{ flex: '1 1 calc(50% - 16px)', minWidth: '220px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 8 }}>
        {sortedLegends.map((entry) => {
          const val = Math.abs(entry.value || 0);
          const pct = total > 0 ? (val / total) * 100 : 0;
          const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
          const formattedVal = formatVolume(entry.value);

          return (
            <div key={entry.key} style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, paddingBottom: 4, paddingTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key], flexShrink: 0 }} />
                  <span style={{ color: '#333', fontWeight: 500 }}>{entry.name}</span>
                  {entry.breakdown && (
                    <Button 
                      type="text" 
                      size="small" 
                      style={{ padding: '0 4px', height: '18px', fontSize: 10, display: 'inline-flex', alignItems: 'center', minWidth: 0, lineHeight: 1 }} 
                      icon={isExchangeExpanded ? <MinusOutlined style={{ fontSize: 9 }} /> : <PlusOutlined style={{ fontSize: 9 }} />}
                      onClick={() => setIsExchangeExpanded(!isExchangeExpanded)}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#777', fontSize: 11 }}>{formattedVal}</span>
                  <span style={{ color: '#111', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{pctString}</span>
                </div>
              </div>
              
              {entry.breakdown && isExchangeExpanded && (
                <div style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 6 }}>
                  {/* GDAM */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                      <span>GDAM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatVolume(entry.breakdown.gdam)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.gdam / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                  {/* DAM */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E67E22' }} />
                      <span>DAM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatVolume(entry.breakdown.dam)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.dam / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                  {/* RTM */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#3498DB' }} />
                      <span>RTM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatVolume(entry.breakdown.rtm)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.rtm / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function TradingDashboard() {
  const f = useFilters();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [viz2, setViz2] = useState('pie');

  // View modes
  const [view1, setView1] = useState('chart');
  const [view2, setView2] = useState('chart');

  // Chart toggles (Absolute vs Percent)
  const [stackMode1, setStackMode1] = useState('absolute');
  const [stackMode2, setStackMode2] = useState('absolute');

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await getVolumeDashboard(f.apiParams());
      setData(aggregateChartData(res.results || [], f.granularity));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (expandedChart) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expandedChart]);


  const kpis = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        total_gdam: '0.00 kWh',
        total_dam: '0.00 kWh',
        total_rtm: '0.00 kWh',
        total_bilateral: '0.00 kWh',
        total_all: '0.00 kWh',
      };
    }

    let totalGdamMu = 0;
    let totalDamMu = 0;
    let totalRtmMu = 0;
    let totalBilateralMu = 0;

    data.forEach(d => {
      totalGdamMu += Number(d.gdam_mu) || 0;
      totalDamMu += Number(d.dam_mu) || 0;
      totalRtmMu += Number(d.rtm_mu) || 0;
      totalBilateralMu += (Number(d.bilat1_mu) || 0) + (Number(d.bilat2_mu) || 0) + (Number(d.bilat3_mu) || 0);
    });

    return {
      total_gdam: formatVolumeNumber(totalGdamMu),
      total_dam: formatVolumeNumber(totalDamMu),
      total_rtm: formatVolumeNumber(totalRtmMu),
      total_bilateral: formatVolumeNumber(totalBilateralMu),
      total_all: formatVolumeNumber(totalGdamMu + totalDamMu + totalRtmMu + totalBilateralMu),
    };
  }, [data]);

  // ------------------------- Chart 1 Configuration -------------------------
  const columns1 = useMemo(() => getTableColumns(f.granularity, [
    { title: 'GDAM Volume (kWh)', dataIndex: 'gdam', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
    { title: 'DAM Volume (kWh)', dataIndex: 'dam', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
    { title: 'RTM Volume (kWh)', dataIndex: 'rtm', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
  ]), [f.granularity]);

  // ------------------------- Chart 2 Configuration -------------------------
  const columns2 = useMemo(() => getTableColumns(f.granularity, [
    { title: 'Exchange Volume (kWh)', dataIndex: 'exchange', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
    { title: 'Contracts Volume (kWh)', dataIndex: 'contracts', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
    { title: 'Goa (kWh)', dataIndex: 'bilat1', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
    { title: 'Nidar (kWh)', dataIndex: 'bilat2', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
    { title: 'NPCL (kWh)', dataIndex: 'bilat3', render: v => v != null && typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v ?? '-' },
  ]), [f.granularity]);

  const isHourlyOr15Min = f.granularity === 'hourly' || f.granularity === '15min' || f.granularity === '15min-raw';
  const axisInterval = f.granularity === '15min' ? 3 : (f.granularity === '15min-raw' ? 2 : 0);
  const axisTickProps = isHourlyOr15Min
    ? { fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }
    : { fontSize: 11, angle: -45, textAnchor: 'end', dy: 15 };

  const brushProps = {};

  const formatTick = v => {
    if (!v) return '';
    if (f.granularity === '15min-raw') {
      if (typeof v === 'string' && v.includes(' ')) {
        return v.split(' ')[1];
      }
      return v;
    }
    if (f.granularity === 'hourly' || f.granularity === '15min') return v;
    const d = dayjs(v);
    if (!d.isValid()) return v;
    if (f.granularity === 'monthly') return d.format('MMM-YY');
    if (f.granularity === 'yearly') return d.format('YYYY');
    return d.format('DD-MMM');
  };

  const chart2Data = useMemo(() => data.map(d => {
    const exchange = (d.gdam || 0) + (d.dam || 0) + (d.rtm || 0);
    const contracts = (Number(d.bilat1) || 0) + (Number(d.bilat2) || 0) + (Number(d.bilat3) || 0);
    return {
      ...d,
      exchange,
      contracts
    };
  }), [data]);

  const tableData1 = useMemo(() => formatTableData(data, f.granularity), [data, f.granularity]);
  const tableData2 = useMemo(() => formatTableData(chart2Data, f.granularity), [chart2Data, f.granularity]);

  const pieData2 = useMemo(() => {
    let exchangeTotal = 0;
    let contractsTotal = 0;
    let damTotal = 0;
    let gdamTotal = 0;
    let rtmTotal = 0;
    chart2Data.forEach(d => {
      exchangeTotal += Number(d.exchange) || 0;
      contractsTotal += (Number(d.bilat1) || 0) + (Number(d.bilat2) || 0) + (Number(d.bilat3) || 0);
      damTotal += Number(d.dam) || 0;
      gdamTotal += Number(d.gdam) || 0;
      rtmTotal += Number(d.rtm) || 0;
    });
    return [
      { 
        name: 'Exchange', 
        value: exchangeTotal, 
        key: 'exchange',
        breakdown: { dam: damTotal, gdam: gdamTotal, rtm: rtmTotal }
      },
      { name: 'Contracts', value: contractsTotal, key: 'contracts' },
    ];
  }, [chart2Data]);

  const colors2 = { exchange: '#3498DB', contracts: '#8E44AD' };

  return (
    <div id="dashboard-volume" style={{ display: 'flex', minHeight: '100vh' }}>
      <div style={{ flex: 1, padding: '12px 24px 24px', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Volume Analysis</Typography.Title>
        </div>

        <Spin spinning={loading}>
          {/* KPI Cards Layout */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <KpiCardV2 title="DAM Volume" value={kpis.total_dam} color="#EAEDED" />
            <KpiCardV2 title="RTM Volume" value={kpis.total_rtm} color="#FDF2E9" />
            <KpiCardV2 title="GDAM Volume" value={kpis.total_gdam} color="#EBF5FB" />
            <KpiCardV2 title="Bilateral Volume" value={kpis.total_bilateral} color="#F5EEF8" />
            <KpiCardV2 title="Total Volume" value={kpis.total_all} color="#D5F5E3" />
          </div>

          {/* Chart 1: Volume Mix: All Market Segments */}
          <Card id="chart-vol-exchange" title="Volume Mix: Exchange" size="small" style={expandedChart === 'chart1' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
            extra={
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Radio.Group value={stackMode1} onChange={e => setStackMode1(e.target.value)} size="small" optionType="button" buttonStyle="solid">
                  <Radio.Button value="absolute">Absolute</Radio.Button>
                  <Radio.Button value="expand">% Share</Radio.Button>
                </Radio.Group>
                <Radio.Group value={view1} onChange={e => setView1(e.target.value)} size="small">
                  <Radio.Button value="chart">Chart</Radio.Button>
                  <Radio.Button value="table">Table</Radio.Button>
                </Radio.Group>
                {view1 === 'chart' ? (
                  <DownloadButton onPng={() => exportCardAsPng('chart-vol-exchange', `Volume_Exchange_${dayjs().format('MMMYYYY')}`)} />
                ) : (
                  <DownloadButton onCsv={() => exportTableAsCsv(tableData1, columns1, `Volume_Exchange_${dayjs().format('MMMYYYY')}`)} />
                )}
                <Button icon={expandedChart === 'chart1' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart1' ? null : 'chart1')} />
              </div>
            }>
            {view1 === 'chart' ? (
              <div style={{ height: expandedChart === 'chart1' ? 'calc(100vh - 120px)' : 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data} stackOffset={stackMode1 === 'expand' ? 'expand' : 'none'}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                    <YAxis width={80} tick={{ fontSize: 11 }} tickFormatter={(tick) => stackMode1 === 'expand' ? `${(tick * 100).toFixed(0)}%` : formatNumberKM(tick)} label={{ value: stackMode1 === 'expand' ? 'Share (%)' : 'Volume (kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                    <Tooltip shared={false} itemSorter={tooltipSorter} labelFormatter={(label) => {
                      if (f.granularity === '15min-raw') {
                        const d = dayjs(label);
                        return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                      }
                      if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                      const d = dayjs(label);
                      if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                      if (f.granularity === 'yearly') return d.format('YYYY');
                      return d.format('DD MMM, YYYY');
                    }} formatter={(v, n, entry) => {
                      if (stackMode1 === 'expand') {
                        const payload = entry?.payload || {};
                        const total = (payload.gdam || 0) + (payload.dam || 0) + (payload.rtm || 0);
                        const pct = total > 0 ? (Number(v) / total) * 100 : 0;
                        return [`${pct.toFixed(2)}%`, n];
                      }
                      return [formatVolumeNumber(v), n];
                    }} />
                    <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                    <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                    <Area type="monotone" dataKey="gdam" name="GDAM" stackId="1" stroke="#2ECC71" fill="#2ECC71" />
                    <Area type="monotone" dataKey="dam" name="DAM" stackId="1" stroke="#E67E22" fill="#E67E22" />
                    <Area type="monotone" dataKey="rtm" name="RTM" stackId="1" stroke="#3498DB" fill="#3498DB" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Table dataSource={tableData1} columns={columns1} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
            )}
          </Card>

          {/* Chart 2: Bilateral Volume Breakdown */}
          <Card id="chart-vol-bilateral"
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>Volume Breakdown</span>
                <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setViz2('trend')} disabled={viz2 === 'trend'} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{viz2 === 'trend' ? 'Trend Chart' : 'Pie Chart'}</Typography.Text>
                <Button type="text" size="small" icon={<RightOutlined />} onClick={() => setViz2('pie')} disabled={viz2 === 'pie'} />
              </div>
            }
            size="small" style={expandedChart === 'chart2' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
            extra={
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Radio.Group value={stackMode2} onChange={e => setStackMode2(e.target.value)} size="small" optionType="button" buttonStyle="solid">
                  <Radio.Button value="absolute">Absolute</Radio.Button>
                  <Radio.Button value="expand">% Share</Radio.Button>
                </Radio.Group>
                <Radio.Group value={view2} onChange={e => setView2(e.target.value)} size="small">
                  <Radio.Button value="chart">Chart</Radio.Button>
                  <Radio.Button value="table">Table</Radio.Button>
                </Radio.Group>
                {view2 === 'chart' ? (
                  <DownloadButton onPng={() => exportCardAsPng('chart-vol-bilateral', `Volume_Bilateral_${dayjs().format('MMMYYYY')}`)} />
                ) : (
                  <DownloadButton onCsv={() => exportTableAsCsv(tableData2, columns2, `Volume_Bilateral_${dayjs().format('MMMYYYY')}`)} />
                )}
                <Button icon={expandedChart === 'chart2' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart2' ? null : 'chart2')} />
              </div>
            }>
            {view2 === 'chart' ? (
              <div style={{ height: expandedChart === 'chart2' ? 'calc(100vh - 120px)' : 360 }}>
                {viz2 === 'pie' ? (
                  <DonutChartWithLegend data={pieData2} colors={colors2} title="Total Volume" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart2Data} stackOffset={stackMode2 === 'expand' ? 'expand' : 'none'}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                      <YAxis width={80} tick={{ fontSize: 11 }} tickFormatter={(tick) => stackMode2 === 'expand' ? `${(tick * 100).toFixed(0)}%` : formatNumberKM(tick)} label={{ value: stackMode2 === 'expand' ? 'Share (%)' : 'Volume (kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                      <Tooltip shared={true} itemSorter={tooltipSorter} labelFormatter={(label) => {
                        if (f.granularity === '15min-raw') {
                          const d = dayjs(label);
                          return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                        }
                        if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                        const d = dayjs(label);
                        if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                        if (f.granularity === 'yearly') return d.format('YYYY');
                        return d.format('DD MMM, YYYY');
                      }} formatter={(v, n, entry) => {
                        if (stackMode2 === 'expand') {
                          const payload = entry?.payload || {};
                          const total = (payload.exchange || 0) + (payload.contracts || 0);
                          const pct = total > 0 ? (Number(v) / total) * 100 : 0;
                          return [`${pct.toFixed(2)}%`, n];
                        }
                        return [formatVolumeNumber(v), n];
                      }} />
                      <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                      <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />

                      {/* Exchange layer */}
                      <Bar dataKey="exchange" name="Exchange" stackId="1" fill="#3498DB" />

                      {/* Contracts layer */}
                      <Bar dataKey="contracts" name="Contracts" stackId="1" fill="#8E44AD" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            ) : (
              <Table dataSource={tableData2} columns={columns2} size="small" scroll={{ x: 'max-content', y: 320 }} pagination={false} rowKey="period" />
            )}
          </Card>

        </Spin>
      </div>

      {/* Right Section: Filters Panel */}
      <GlobalFilterBar onApply={fetchData} />
    </div>
  );
}
